import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Brush,
  ResponsiveContainer,
} from 'recharts';
import { ILoader } from '~/_base/components/helixIcons';
import isNil from '~/_base/functions/isNil';
import getFiltersFromLocalStorage from '../functions/getFiltersFromLocalStorage';
import { LocalisationTimeFormats } from '~/_base/help/localisationHelper';

export default function ZoomableChart(props) {
  const [state, setState] = useState({
    initialised: false,
    chartData: [],
    timeRange: null,
    brushRange: null,
    defaultTimeRange: null,
    maxValue: 0
  });

  const [timeout, setTimeoutRef] = useState(null);

  useEffect(() => {
    if (!state.initialised && props.eventActivity && props.eventStart) {
      init(props);
    }
  }, [props.eventActivity, props.eventStart, state.initialised]);

  const init = useCallback((props) => {
    const interval = 'hour';
    const startDate = props.eventStart;
    let endDate = null;
    let m = moment(startDate).startOf('hour');
    let chartData = [];

    for (let i = 0; i < props.eventActivity.length; i++) {
      const timeValue = m.valueOf();
      chartData.push({
        time: timeValue,
        value: props.eventActivity[i],
        index: i
      });
      m.add(1, interval);
      endDate = m;
    }

    chartData.sort((a, b) => a.time - b.time);
    const maxValue = Math.max(...props.eventActivity);

    const beginTime = endDate.clone().subtract(7, 'd');
    let initialRange = {
      start: beginTime.valueOf(),
      end: endDate.valueOf()
    };

    const filters = getFiltersFromLocalStorage(props.projectId);
    if (filters !== null && !isNil(filters.startTime)) {
      initialRange = {
        start: moment(filters.startTime).valueOf(),
        end: moment(filters.endTime).valueOf()
      };
    }

    const startTime = moment(initialRange.start).format('YYYY-MM-DDTHH:mm:ss');
    const endTime = moment(initialRange.end).format('YYYY-MM-DDTHH:mm:ss');

    props.actions.getFeedData({ startTime, endTime });
    setTimeout(() => {
      props.actions.getMapData({ startTime, endTime });
    }, 200);

    setState({
      initialised: true,
      chartData,
      timeRange: initialRange,
      brushRange: initialRange,
      defaultTimeRange: initialRange,
      maxValue
    });
  }, []);

  const handleTimeRangeChange = useCallback((brushData) => {
    if (timeout) clearTimeout(timeout);

    if (!brushData) {
      setState(prev => ({
        ...prev,
        timeRange: prev.defaultTimeRange,
        brushRange: prev.defaultTimeRange
      }));
      return;
    }

    const { startIndex, endIndex } = brushData;
    
    if (typeof startIndex === 'number' && typeof endIndex === 'number' && 
        startIndex >= 0 && endIndex < state.chartData.length && startIndex <= endIndex) {
      
      const startTime = state.chartData[startIndex]?.time;
      const endTime = state.chartData[endIndex]?.time;
      
      if (startTime && endTime) {
        const maxDaysOfRange = 365;
        const period = moment(endTime).diff(moment(startTime), 'days') + 1;
        
        let finalStartTime = startTime;
        let finalEndTime = endTime;
        
        if (period > maxDaysOfRange) {
          finalStartTime = moment(endTime).subtract(maxDaysOfRange, 'days').valueOf();
          finalEndTime = endTime;
        }

        const newRange = { start: finalStartTime, end: finalEndTime };

        setState(prev => ({
          ...prev,
          timeRange: newRange,
          brushRange: newRange
        }));

        // Debounced update
        const newTimeout = setTimeout(() => {
          const formattedStart = moment(finalStartTime).format('YYYY-MM-DDTHH:mm:ss');
          const formattedEnd = moment(finalEndTime).format('YYYY-MM-DDTHH:mm:ss');
          
          props.actions.getFeedData({ startTime: formattedStart, endTime: formattedEnd });
          setTimeout(() => {
            props.actions.getMapData({ startTime: formattedStart, endTime: formattedEnd });
          }, 200);
        }, 300);
        setTimeoutRef(newTimeout);
      }
    }
  }, [state.chartData, timeout, props.actions]);

  const generateDateRangeText = () => {
    if (!state.chartData.length) return '';
    
    const { startIndex, endIndex } = getBrushIndices();
    const startTime = moment(state.chartData[startIndex]?.time).format(
      LocalisationTimeFormats().dby_hm
    );
    const endTime = moment(state.chartData[endIndex]?.time).format(
      LocalisationTimeFormats().dby_hm
    );

    return `${startTime} to ${endTime}`;
  };

  const formatMonthTick = (tickItem) => {
    return moment(tickItem).format('MMM');
  };

  const formatYearTick = (tickItem) => {
    return moment(tickItem).format('YYYY');
  };

  const generateMonthlyTicks = () => {
    if (!state.chartData.length) return [];

    const start = moment(state.chartData[0].time).startOf('month');
    const end = moment(state.chartData[state.chartData.length - 1].time).endOf('month');

    const ticks = [];
    let current = start.clone();

    while (current.isSameOrBefore(end)) {
      ticks.push(current.valueOf());
      current.add(1, 'month');
    }

    return ticks;
  };

  const generateYearlyTicks = () => {
    if (!state.chartData.length) return [];
    const start = moment(state.chartData[0].time).startOf('year');
    const end = moment(state.chartData[state.chartData.length - 1].time).endOf('year');
    const ticks = [];
    let current = start.clone();
    while (current.isSameOrBefore(end)) {
      ticks.push(current.valueOf());
      current.add(1, 'year');
    }
    return ticks;
  };

  const getZoomedData = () => state.chartData;

  const getBrushIndices = () => {
    if (!state.brushRange || !state.chartData.length) {
      return { startIndex: 0, endIndex: state.chartData.length - 1 };
    }

    const startIndex = Math.max(0, 
      state.chartData.findIndex(d => d.time >= state.brushRange.start)
    );
    
    let endIndex = state.chartData.findIndex(d => d.time > state.brushRange.end);
    if (endIndex === -1) endIndex = state.chartData.length - 1;
    else endIndex = Math.max(0, endIndex - 1);

    return { startIndex, endIndex };
  };

  if (props.getting) {
    return (
      <div className="timeSelectorWrapper">
        <div className="mapLoading">
          <ILoader fontSize="large" />
        </div>
      </div>
    );
  }

  if (!state.initialised || !props.renderChart) {
    return <div className="timeSelectorWrapper" />;
  }

  const zoomedData = getZoomedData();
  const { startIndex, endIndex } = getBrushIndices();
  const monthlyTicks = generateMonthlyTicks();
  const yearlyTicks = generateYearlyTicks();

  return (
    <div className="timeSelectorWrapper">
      <style>{`
        .recharts-brush .recharts-brush-traveller {
          cursor: ew-resize !important;
          pointer-events: all !important;
        }
        .recharts-brush .recharts-brush-slide {
          cursor: move !important;
          pointer-events: all !important;
        }
        .recharts-brush {
          pointer-events: all !important;
        }
        .recharts-surface {
          overflow: visible !important;
        }
      `}</style>
      <div>
        {/* Zoomed Chart */}
        <div className="lineChart">
          <ResponsiveContainer width="100%" height={100}>
            <LineChart data={zoomedData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
              <XAxis 
                dataKey="time"
                type="number"
                scale="time"
                domain={[
                  state.timeRange?.start || 'dataMin',
                  state.timeRange?.end || 'dataMax'
                ]}
                tickFormatter={formatMonthTick}
                tick={{ fontSize: 10 }}
                ticks={monthlyTicks}
              />
              <YAxis
                domain={[0, state.maxValue || 'dataMax']}
                width={30}
                tick={{ fontSize: 10 }}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke="red"
                strokeWidth={1}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="lineChart">
          <ResponsiveContainer width="100%" height={150}>
            <LineChart
              data={state.chartData}
              margin={{ top: 5, right: 5, left: 5, bottom: 45 }}
            >
              <XAxis
                dataKey="time"
                type="number"
                scale="time"
                domain={['dataMin', 'dataMax']}
                tickFormatter={formatYearTick}
                tick={{ fontSize: 10 }}
                ticks={yearlyTicks}
              />
              <YAxis
                domain={[0, state.maxValue || 'dataMax']}
                width={30}
                tick={{ fontSize: 10 }}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#3f85cb"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
              <Brush
                dataKey="time"
                height={40}
                onChange={handleTimeRangeChange}
                tickFormatter={formatYearTick}
                startIndex={startIndex}
                endIndex={endIndex}
                stroke="#3f85cb"
                fill="#3f85cb"
                fillOpacity={0.4}
                travellerWidth={12}
                gap={2}
                traveller={{
                  fill: '#3f85cb',
                  stroke: '#0052CC',
                  strokeWidth: 2
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="timeDateRange">
          {generateDateRangeText()}
        </div>
      </div>
    </div>
  );
}
